/**
 * Manejar los menús con el cuerpo (control por cámara):
 *   brazo izquierdo arriba → pasar al siguiente botón (como el tabulador)
 *   brazo derecho arriba   → pulsar el botón marcado
 * El botón marcado lleva la clase .nav-focus. Al cambiar de pantalla se marca el botón principal y,
 * durante un instante, se ignoran los brazos: así un giro hecho justo al morir no pulsa nada.
 */
const GRACE_MS = 900;

export class BodyNav {
  constructor(hint) {
    this.hint = hint; // aviso «brazo izq.: cambiar · brazo der.: elegir»
    this.screen = null;
    this.focused = null;
    this.index = 0;
    this.active = false;
    this.since = 0;
  }

  /** Pantalla visible (elemento) o null si no hay ninguna. */
  setScreen(el) {
    this.screen = el;
    this.since = performance.now();
    this.#mark(null);
    this.focused = null;
    if (el && this.active) this.focusDefault();
    this.#updateHint();
  }

  /** Activa o desactiva la navegación (cámara lista o no). */
  setActive(on) {
    if (on === this.active) return;
    this.active = on;
    if (on && this.screen) {
      this.since = performance.now();
      this.focusDefault();
    } else this.#mark(null);
    this.#updateHint();
  }

  /** Brazo izquierdo: siguiente botón. Devuelve true si lo ha usado. */
  next() {
    if (!this.#usable()) return false;
    const list = this.#buttons();
    if (!list.length) return false;
    let i = list.indexOf(this.focused);
    if (i < 0) i = Math.min(this.index, list.length - 1) - 1;
    this.#focus(list, (i + 1) % list.length);
    return true;
  }

  /** Brazo derecho: pulsa el botón marcado. Devuelve true si lo ha usado. */
  select() {
    if (!this.#usable()) return false;
    let target = this.focused;
    if (!target?.isConnected || !target.offsetParent) {
      // El botón desapareció (p. ej. se redibujó el vestuario): el que ocupa su lugar.
      const list = this.#buttons();
      target = list[Math.min(this.index, list.length - 1)];
      if (!target) return false;
      this.#focus(list, list.indexOf(target));
    }
    target.click();
    // Si al pulsar se redibuja la lista, se vuelve a marcar el botón de la misma posición.
    requestAnimationFrame(() => {
      if (this.focused && !this.focused.isConnected) {
        const list = this.#buttons();
        if (list.length) this.#focus(list, Math.min(this.index, list.length - 1));
      }
    });
    return true;
  }

  #usable() {
    return this.active && this.screen && performance.now() - this.since > GRACE_MS;
  }

  #buttons() {
    return [...this.screen.querySelectorAll('button')].filter((b) => b.offsetParent !== null && !b.disabled);
  }

  /** Marca el botón principal de la pantalla (o el primero). */
  focusDefault() {
    if (!this.active || !this.screen) return;
    const list = this.#buttons();
    const primary = list.find((b) => b.classList.contains('btn-primary'));
    this.#focus(list, primary ? list.indexOf(primary) : 0);
  }

  #focus(list, i) {
    const b = list[i] ?? null;
    this.index = Math.max(0, i);
    this.#mark(b);
    this.focused = b;
    b?.scrollIntoView?.({ block: 'nearest' });
  }

  #mark(b) {
    this.focused?.classList.remove('nav-focus');
    b?.classList.add('nav-focus');
  }

  #updateHint() {
    if (this.hint) this.hint.hidden = !(this.active && this.screen);
  }
}
